import React, { useEffect, useState } from 'react';
import { reduxForm, Field } from 'redux-form';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Link } from 'react-router-dom';
import { notification } from 'antd';
import { Strings } from '../../dataProvider/localize'
import * as actions from '../../actions';
import { customInput } from '../common/custom-input';
import { validate } from '../../utils/validations';
import GoogleCaptcha from '../common/GoogleCaptcha';
import {
	JWT_SESSION_TOKEN,
	JWT_ID_TOKEN,
	JWT_ACCESS_TOKEN,
	REMEMBER_ME,
	REMEMBER_ME_DATA
} from '../../dataProvider/constant';
import logo from "../../assets/images/fm-farm-logo.png";

import { getStorage, setStorage, authRedirect, handleFocus, DeepTrim } from '../../utils/common';
import { resolvePostLoginRedirectPath } from '../../utils/authRedirect';
import { getNextSignInPath, isSignInSubmitDisabled } from './signInUtils';
import styles from "./Auth.module.css";
import "./AuthStyles.css";

const EyeIcon = () => (
	<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" width="24" height="24">
		<path fill="currentColor" d="M320 96C239.2 96 174.5 132.8 127.4 176.6C80.6 220.1 49.3 272 34.4 307.7C31.1 315.6 31.1 324.4 34.4 332.3C49.3 368 80.6 420 127.4 463.4C174.5 507.1 239.2 544 320 544C400.8 544 465.5 507.2 512.6 463.4C559.4 419.9 590.7 368 605.6 332.3C608.9 324.4 608.9 315.6 605.6 307.7C590.7 272 559.4 220 512.6 176.6C465.5 132.9 400.8 96 320 96zM176 320C176 240.5 240.5 176 320 176C399.5 176 464 240.5 464 320C464 399.5 399.5 464 320 464C240.5 464 176 399.5 176 320zM320 256C320 291.3 291.3 320 256 320C244.5 320 233.7 317 224.3 311.6C223.3 322.5 224.2 333.7 227.2 344.8C240.9 396 293.6 426.4 344.8 412.7C396 399 426.4 346.3 412.7 295.1C400.5 249.4 357.2 220.3 311.6 224.3C316.9 233.6 320 244.4 320 256z" />
	</svg>
);

const EyeSlashIcon = () => (
	<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" width="24" height="24">
		<path fill="currentColor" d="M73 39.1C63.6 29.7 48.4 29.7 39.1 39.1C29.8 48.5 29.7 63.7 39 73.1L567 601.1C576.4 610.5 591.6 610.5 600.9 601.1C610.2 591.7 610.3 576.5 600.9 567.2L504.5 470.8C507.2 468.4 509.9 466 512.5 463.6C559.3 420.1 590.6 368.2 605.5 332.5C608.8 324.6 608.8 315.8 605.5 307.9C590.6 272.2 559.3 220.2 512.5 176.8C465.4 133.1 400.7 96.2 319.9 96.2C263.1 96.2 214.3 114.4 173.9 140.4L73 39.1zM236.5 202.7C260 185.9 288.9 176 320 176C399.5 176 464 240.5 464 320C464 351.1 454.1 379.9 437.3 403.5L402.6 368.8C415.3 347.4 419.6 321.1 412.7 295.1C399 243.9 346.3 213.5 295.1 227.2C286.5 229.5 278.4 232.9 271.1 237.2L236.4 202.5zM357.3 459.1C345.4 462.3 332.9 464 320 464C240.5 464 176 399.5 176 320C176 307.1 177.7 294.6 180.9 282.7L101.4 203.2C68.8 240 46.4 279 34.5 307.7C31.2 315.6 31.2 324.4 34.5 332.3C49.4 368 80.7 420 127.5 463.4C174.6 507.1 239.3 544 320.1 544C357.4 544 391.3 536.1 421.6 523.4L357.4 459.2z" />
	</svg>
);

export const SignIn = ({
	handleSubmit,
	history,
	location,
	resetReduxStore,
	signIn,
	cleanErrorMsg,
	errorMessage,
	reset,
	change,
}) => {
	const [count, setCount] = useState(0);
	const [fromChild, setFromChild] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [rememberMeChecked, setRememberMeChecked] = useState(Boolean(JSON.parse(getStorage(REMEMBER_ME) || 'false')));
	const rememberedUserName = getStorage(REMEMBER_ME_DATA);
	const hasRememberedUserName = !!rememberedUserName;
	const isCaptchaRequired = count > 2;
	const isSubmitDisabled = isSignInSubmitDisabled({ count, fromChild, isSubmitting });

	useEffect(() => {
		document.title = "Login to your Organization - FM Farm";
		authRedirect(history);
		resetReduxStore();
	}, [history, resetReduxStore]);

	const onSubmit = async (formData) => {
		if (isSubmitting) {
			return;
		}

		setIsSubmitting(true);

		try {
			formData = await DeepTrim(formData);
			const postLoginRedirectPath = resolvePostLoginRedirectPath(location.search);
			const newFormData = {
				...formData,
				email_address: formData.user_name,
				request_from: "WEB"
			};

			const res = await signIn(newFormData);
			const response = res && res.data ? res.data : {};
			const authData = response.data ? response.data : {};
			const nextPath = getNextSignInPath({
				status: response.status,
				authType: authData.type,
				hasSessionToken: !!getStorage(JWT_SESSION_TOKEN),
				hasAccessToken: !!getStorage(JWT_ACCESS_TOKEN),
				hasIdToken: !!getStorage(JWT_ID_TOKEN),
				postLoginRedirectPath,
			});

			cleanErrorMsg();
			if (response.status) {
				setStorage(REMEMBER_ME, rememberMeChecked);
			}

			if (nextPath) {
				history.replace(nextPath);
			}
		} catch (message) {
			setCount((prevCount) => prevCount + 1);
			if (!message) {
				notification.error({
					message: Strings.error_title,
					description: Strings.generic_error,
					onClick: () => { },
					className: 'ant-error'
				});
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleData = (data) => {
		setFromChild(data);
	};

	const handleRememberMe = (value) => {
		setRememberMeChecked(value);
	};

	const togglePasswordVisibility = () => {
		setShowPassword((prevValue) => !prevValue);
	};

	const handleNotMeClick = () => {
		reset();
		localStorage.removeItem(REMEMBER_ME_DATA);
		change("user_name", "");
		setCount(0);
		setFromChild('');
		cleanErrorMsg();
	};

	const passwordVisibilityButton = (
		<button
			type="button"
			onClick={togglePasswordVisibility}
			aria-label={showPassword ? 'Hide password' : 'Show password'}
			aria-pressed={showPassword}
			className={styles.PasswordToggleButton}
		>
			{showPassword ? <EyeSlashIcon /> : <EyeIcon />}
		</button>
	);

	return (
		<div className="default-v2-container">
			<div className={styles.Background}>
				<div className="container-fluid vh-100">
					<div className="row h-100">
						<div className="col m-auto">
							<div className={`${styles.LoginCard} p-5 text-center mx-auto`}>
								<img src={logo} alt="FM Farm" style={{ maxHeight: "70px", width: "auto" }} />
								<h2 className={styles.LoginHeading}>Login</h2>
								<form onSubmit={handleSubmit(onSubmit)}>
									<fieldset className="userTxtbox fldset-box outr-notme">
										<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="24" height="24" className="svg-ico-f">
											<path d="M224 256A128 128 0 1 0 224 0a128 128 0 1 0 0 256zm-45.7 48C79.8 304 0 383.8 0 482.3C0 498.7 13.3 512 29.7 512H418.3c16.4 0 29.7-13.3 29.7-29.7C448 383.8 368.2 304 269.7 304H178.3z" />
										</svg>
										<Field
											name="user_name"
											type="text"
											id="user_name"
											autoFocus
											className={styles.InputFieldV2}
											placeholder={Strings.sign_in_email_placeholder}
											autoComplete="username"
											readonly={hasRememberedUserName}
											component={customInput} />
										{hasRememberedUserName ? <button className="notmetxt" onClick={handleNotMeClick} type="button">Not me</button> : null}
									</fieldset>
									<fieldset className="passTxtbox fldset-box">
										<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" className="svg-ico-f" width="24" height="24" >
											<path d="M336 352c97.2 0 176-78.8 176-176S433.2 0 336 0S160 78.8 160 176c0 18.7 2.9 36.8 8.3 53.7L7 391c-4.5 4.5-7 10.6-7 17v80c0 13.3 10.7 24 24 24h80c13.3 0 24-10.7 24-24V448h40c13.3 0 24-10.7 24-24V384h40c6.4 0 12.5-2.5 17-7l33.3-33.3c16.9 5.4 35 8.3 53.7 8.3zM376 96a40 40 0 1 1 0 80 40 40 0 1 1 0-80z" /></svg>
										<Field
											name="password"
											type={showPassword ? 'text' : 'password'}
											id="password"
											className={styles.InputFieldV2}
											placeholder={Strings.sign_in_password_label}
											autoComplete="current-password"
											{...(hasRememberedUserName ? { autoFocus: true } : {})}
											suffix={passwordVisibilityButton}
											component={customInput} />
									</fieldset>
									{errorMessage ?
										<div className="alert alert-danger">
											{errorMessage}
										</div> : null}
									{isCaptchaRequired ?
										<div className="sf-g-captcah">
											<GoogleCaptcha handlerFromParant={handleData} />
										</div> :
										null}
									<button type="submit" className={styles.SigninBtn} disabled={isSubmitDisabled}>{Strings.sign_in_btn_sign_in}</button>
									{isCaptchaRequired ?
										<div className="forgt-pass">{Strings.sign_in_forgot_password} <Link onClick={cleanErrorMsg}
											key='forgotpass' to='/forgotpass' className={styles.ForgotLink}>{Strings.sign_in_password}</Link> | <Link
												onClick={cleanErrorMsg}
												key='forgotusername' className={styles.ForgotLink} to='/Forgotusername'>{Strings.sign_in_forgot_username}</Link>
										</div> : null}
									<div className="rember-me">
										<input className="auth-v2-checkbox" type="checkbox" value="value1" checked={rememberMeChecked} onChange={(e) => handleRememberMe(e.target.checked)} />
										<label className={styles.RememberMe} htmlFor="styled-checkbox-1" style={{ cursor: "pointer" }} onClick={() => handleRememberMe(!rememberMeChecked)}>{Strings.sign_in_remember_me}</label>
									</div>
								</form>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

const mapStateToProps = (state) => {
	if (getStorage(REMEMBER_ME_DATA)) {
		var initialValues = { user_name: getStorage(REMEMBER_ME_DATA) };
	}
	return {
		errorMessage: state.auth.errorMessage,
		initialValues: initialValues ? initialValues : {}
	}
}

export const SignInComponent = connect(mapStateToProps, actions)(SignIn);
export default compose(
	connect(mapStateToProps, actions),
	reduxForm({
		form: 'signin', validate,
		onSubmitFail: (errors) => {
			handleFocus(errors, "#");
		}
	})
)(SignIn)